import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { Platform, StyleSheet, FlatList, Alert, Pressable, useWindowDimensions } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { Title, View, Text, Card } from '@constants/Themed';
import SalesStatWidget from './Components/SalesStatWidget';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import DataSetChart from '@components/dataTables/Charts/DataSetChart';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import WebsiteAnalytics from '../WelcomeScreen/Widgets/WebsiteAnalytics';
import LoadingIndicator from '@components/Global/Spinner';
import AchievementBackground from '@assets/--native/AchievementBackground';
// import Button from '@components/UI/Button/Button';
import { AlertDialogActionProps, Button, H6 } from 'tamagui';
import { AlertDialog, H2, Separator, SizableText, Tabs, TabsContentProps, XStack, YStack, ScrollView } from 'tamagui';
import { AntDesign, Entypo, MaterialIcons } from '@expo/vector-icons';
import SwipeableRobot, { RobotHandle } from '@components/RobotHelper/SwipeableRobot';
import StyledPieChart from '@components/dataTables/Charts/PieChart.native';
import apis from '@api/api';
import { useNavigation } from '@react-navigation/native';
import { useDeviceType } from '@hooks/useDeviceType';
import { Virtuoso } from 'react-virtuoso';
import InfoBox from '@components/Global/InfoBox';
import ConnectedPlatforms from './Components/ConnectedPlatforms';
import { selectUser } from "@reduxLocal/selectors/userSelectors";
import { useIsStripeConnected } from '@hooks/useStripeConnection';
import { usePaymentProvider } from '@hooks/usePaymentProvider';
import SalesStatsOverview from './Components/SalesStatsOverview';
import SalesChart from './Components/SalesChart';
import RecentTransactions from './Components/RecentTransactions';
import PaymentMethodStats from './Components/PaymentMethodStats';

const allowedList = ['linkedin', 'tiktok', 'instagram', 'x', 'facebook', 'whatsapp'];

const SalesDashboard = forwardRef<any, any>((props, ref) => {
  const theme = themeSelector();
  const queryClient = useQueryClient();
  const [statsLoading, setStatsLoading] = useState(true);
  const robotRef = useRef<RobotHandle>(null);
  const navigation = useNavigation();
  const [open, setOpen] = useState(false);
  const userIntegrations = selectUser().authentications;
  const { isTablet } = useDeviceType();
  const { width: screenWidth } = useWindowDimensions();
  const originalIntegrationData = queryClient.getQueryData(['integrationData']);
  const isStripeConnected = useIsStripeConnected();
  const { effectiveProvider, hasStripe, hasPayStack } = usePaymentProvider();

    // Responsive breakpoints
    const isLargeScreen = screenWidth >= 1200;
    const isMediumScreen = screenWidth >= 768 && screenWidth < 1200;
    const isSmallScreen = screenWidth < 768;

    // Responsive styles
    const getResponsiveStyles = () => ({
      container: {
        maxWidth: '100%' as const,
        width: '100%' as const,
        overflow: 'hidden' as const,
      },
      mainGrid: {
        flexDirection: isSmallScreen ? 'column' : 'row' as 'column' | 'row',
        gap: 24,
        flex: 1,
        width: '100%' as const,
        maxWidth: '100%' as const,
        alignItems: 'flex-start' as const,
      },
      leftColumn: {
        flex: isSmallScreen ? 1 : isLargeScreen ? 2 : 1,
        width: isSmallScreen ? '100%' as const : 'auto' as const,
        minWidth: 0,
      },
      rightColumn: {
        flex: isSmallScreen ? 1 : isLargeScreen ? 3 : 2,
        width: isSmallScreen ? '100%' as const : 'auto' as const,
        minWidth: 0,
      },
    });

    // Payment Provider Sales Data Queries - Stripe
    const { data: stripeSalesStats, isLoading: stripeSalesStatsLoading } = useQuery({
      queryKey: ['stripeSalesStats'],
      queryFn: apis.getStripeSalesStats,
      enabled: effectiveProvider === 'stripe' && hasStripe,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    });

    const { data: stripeMonthlySalesData, isLoading: stripeMonthlySalesLoading } = useQuery({
      queryKey: ['stripeMonthlySales'],
      queryFn: () => apis.getStripeMonthlySalesOverview(12),
      enabled: effectiveProvider === 'stripe' && hasStripe,
      refetchInterval: 5 * 60 * 1000,
    });

    const { data: stripeRecentTransactions, isLoading: stripeTransactionsLoading } = useQuery({
      queryKey: ['stripeRecentTransactions'],
      queryFn: () => apis.getStripeRecentTransactions(20),
      enabled: effectiveProvider === 'stripe' && hasStripe,
      refetchInterval: 2 * 60 * 1000, // More frequent updates for transactions
    });

    const { data: stripePaymentMethodStats, isLoading: stripePaymentStatsLoading } = useQuery({
      queryKey: ['stripePaymentMethodStats'],
      queryFn: apis.getStripePaymentMethodStats,
      enabled: effectiveProvider === 'stripe' && hasStripe,
      refetchInterval: 10 * 60 * 1000,
    });

    // Payment Provider Sales Data Queries - PayStack
    const { data: paystackSalesStats, isLoading: paystackSalesStatsLoading } = useQuery({
      queryKey: ['paystackSalesStats'],
      queryFn: apis.getPayStackSalesStats,
      enabled: effectiveProvider === 'paystack' && hasPayStack,
      refetchInterval: 5 * 60 * 1000,
    });

    const { data: paystackMonthlySalesData, isLoading: paystackMonthlySalesLoading } = useQuery({
      queryKey: ['paystackMonthlySales'],
      queryFn: () => apis.getPayStackMonthlySalesOverview(12),
      enabled: effectiveProvider === 'paystack' && hasPayStack,
      refetchInterval: 5 * 60 * 1000,
    });

    const { data: paystackRecentTransactions, isLoading: paystackTransactionsLoading } = useQuery({
      queryKey: ['paystackRecentTransactions'],
      queryFn: () => apis.getPayStackRecentTransactions(20),
      enabled: effectiveProvider === 'paystack' && hasPayStack,
      refetchInterval: 2 * 60 * 1000,
    });

    const { data: paystackPaymentMethodStats, isLoading: paystackPaymentStatsLoading } = useQuery({
      queryKey: ['paystackPaymentMethodStats'],
      queryFn: apis.getPayStackPaymentMethodStats,
      enabled: effectiveProvider === 'paystack' && hasPayStack,
      refetchInterval: 10 * 60 * 1000,
    });

    // Unified data based on effective provider
    const salesStats = effectiveProvider === 'stripe' ? stripeSalesStats : paystackSalesStats;
    const salesStatsLoading = effectiveProvider === 'stripe' ? stripeSalesStatsLoading : paystackSalesStatsLoading;
    const monthlySalesData = effectiveProvider === 'stripe' ? stripeMonthlySalesData : paystackMonthlySalesData;
    const monthlySalesLoading = effectiveProvider === 'stripe' ? stripeMonthlySalesLoading : paystackMonthlySalesLoading;
    const recentTransactions = effectiveProvider === 'stripe' ? stripeRecentTransactions : paystackRecentTransactions;
    const transactionsLoading = effectiveProvider === 'stripe' ? stripeTransactionsLoading : paystackTransactionsLoading;
    const paymentMethodStats = effectiveProvider === 'stripe' ? stripePaymentMethodStats : paystackPaymentMethodStats;
    const paymentStatsLoading = effectiveProvider === 'stripe' ? stripePaymentStatsLoading : paystackPaymentStatsLoading;

    const textHelp = [
      'Build and manage your campaigns with ease',
      'Track your campaign progress and performance with our advanced analytics tools',
      'Get insights on your campaign performance and make data-driven decisions',
      'Add a new campaign by clicking the + button on the top right corner',
      'Please note, with a current G Suite account, you can send up to a maximum of 2000 emails per day. With a free Gmail account, you can send up to a maximum of 500 emails per day.',
    ]

    const handleShowHelp = () => {
      // setHelp(true);
      robotRef.current!.showHelp();
    }

    const loadingCallback = useCallback(() => {
      setStatsLoading(false)
    }, [statsLoading]);

    const responsiveStyles = getResponsiveStyles();

    const TabsContent = (props: TabsContentProps) => {
      return (
        <Tabs.Content
          key="tab3"
          padding="$2"
          alignItems="center"
          justifyContent="center"
          flex={1}
          borderRadius="$2"
          borderTopLeftRadius={0}
          borderTopRightRadius={0}
          borderWidth="$2"
          {...props}
        >
          {props.children}
        </Tabs.Content>
      )
    }

    const checkTemplate = async () => {
      try {
      // Always fetch fresh data to ensure accuracy
        const data = await queryClient.fetchQuery({
          queryKey: ['dataTemplateCount'],
          queryFn: async () => {
            const result = await apis.getDataTemplateCount();
            // Validate and normalize the response
            if (!result || typeof result !== 'object') {
              console.warn('⚠️ Invalid API response structure:', result);
              return { email: 0, invoice: 0, document: 0, models: 0, utilities: 0 };
            }
            // Ensure all counts are numbers
            return {
              email: Number(result.email) || 0,
              invoice: Number(result.invoice) || 0,
              document: Number(result.document) || 0,
              models: Number(result.models) || 0,
              utilities: Number(result.utilities) || 0,
            };
          },
          staleTime: 0, // Always fetch fresh data
        });
        return data;
      } catch (error) {
        console.error('❌ Error fetching template count:', error);
        // Return zero counts on error to show the alert
        return { email: 0, invoice: 0, document: 0, models: 0, utilities: 0 };
      }
    }

    const handleAlert = () => {
      setOpen(true)
    }


    return (
      <DefaultScreen
        ref={robotRef as any}
        robotHelperProps={{
          textHelp,
          showByDefault: true,
          autoShow: true,
          position: 'bottom-left'
        }}
        searchBar={{
          placeHolder: "Search for a campaign",
          customIcons: [{
            function: () => {
              checkTemplate().then(res => {
                const hasCountAboveZero = Object.values(res || {}).some((item: any) => item > 0);
                if (hasCountAboveZero) {
                  (ref as any)?.current?.openModal();
                } else {
                  handleAlert();
                }
              }).catch(err => {
                handleAlert();
              })
            },
            icon: <AntDesign size={20} name="plus" color="white" />,
            functionName: 'Add Campaign'
          },
          {
            icon: <Entypo name="help" size={18} color={"white"} />,
            function: handleShowHelp,
            functionName: 'Show Help',
          },]
        }}
        containerStyle={{
          gap: 24,
          maxWidth: '100%',
          width: '100%',
          alignSelf: 'center',
        }}
        style={{
          paddingTop: 20,
          paddingBottom: 40,
          paddingHorizontal: isLargeScreen ? 32 : isMediumScreen ? 24 : 16,
          maxWidth: isLargeScreen ? 1400 : '100%',
          width: '100%',
          alignSelf: 'center',
        }}
      >
        <XStack flexWrap='wrap'>
          {/* Modern Dashboard Header */}
          <Animatable.View
            animation="fadeInDown"
            duration={800}
            style={{
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderRadius: 20,
              padding: isLargeScreen ? 32 : isMediumScreen ? 24 : 20,
              marginVertical: 16,
              flex: 3,
              elevation: 10,
              borderWidth: 1,
              borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
            }}
          >
            <XStack style={{ flex: 1, alignItems: 'center', marginBottom: 20 }}>
              <View style={{
                backgroundColor: theme ? "#ddd6fe" : "#312e81",
                borderRadius: 12,
                padding: 12,
                marginRight: 16
              }}>
                <MaterialIcons
                  name="analytics"
                  size={28}
                  color={theme ? "#7c3aed" : "#a78bfa"}
                />
              </View>
              <View>
                <H2 style={{
                  color: theme ? "#1e293b" : "#f1f5f9",
                  fontWeight: '700',
                  marginBottom: 4,
                  fontSize: 24,

                  }}>
                    Sales Dashboard
                  </H2>
                  <Text style={{
                    color: theme ? "#64748b" : "#94a3b8",
                    fontSize: 14
                  }}>
                    Monitor campaigns and track performance
                  </Text>
                </View>
              </XStack>

              {/* Stats Grid */}
              <XStack gap={12} flexWrap="wrap" justifyContent="center" alignItems="center">
                {[
                  {
                    title: 'Active Campaigns',
                    value: '12',
                    icon: 'campaign',
                    color: theme ? "#0ea5e9" : "#38bdf8",
                    bgColor: theme ? "#dbeafe" : "#0c4a6e"
                  },
                  {
                    title: 'Total Interactions',
                    value: '2.4K',
                    icon: 'trending-up',
                    color: theme ? "#059669" : "#10b981",
                    bgColor: theme ? "#d1fae5" : "#064e3b"
                  },
                  {
                    title: 'Conversion Rate',
                    value: '18.5%',
                    icon: 'vertical-align-bottom',
                    color: theme ? "#7c3aed" : "#a78bfa",
                    bgColor: theme ? "#ede9fe" : "#312e81"
                  },
                  {
                    title: 'Connected Platforms',
                    value: Object.keys(userIntegrations).filter(item => allowedList.includes(item)).length.toString(),
                    icon: 'link',
                    color: theme ? "#f59e0b" : "#fbbf24",
                    bgColor: theme ? "#fef3c7" : "#451a03"
                  }
                ].map((stat, index) => (
                  <Animatable.View
                    key={index}
                    animation="bounceIn"
                    delay={index * 200}
                    style={{
                      flexBasis: isLargeScreen ? '22%' : isMediumScreen ? '45%' : '100%',
                      flexGrow: 1,
                      minWidth: isSmallScreen ? '100%' : 140,
                      maxWidth: isLargeScreen ? 220 : isMediumScreen ? 200 : '100%',
                      backgroundColor: stat.bgColor,
                      borderRadius: 16,
                      padding: 16,
                      alignItems: 'center',
                      marginBottom: 8,
                      borderWidth: 1,
                      borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                    }}
                  >
                    <MaterialIcons
                      name={stat.icon as any}
                      size={32}
                      color={stat.color}
                      style={{ marginBottom: 8 }}
                    />
                    <Text style={{
                      fontSize: 24,
                      fontWeight: '800',
                      color: stat.color,
                      marginBottom: 4
                    }}>
                      {stat.value}
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      fontWeight: '600',
                      color: theme ? "#64748b" : "#94a3b8",
                      textAlign: 'center'
                    }}>
                      {stat.title}
                    </Text>
                  </Animatable.View>
                ))}
              </XStack>
            </Animatable.View>
            {/* Marketing Stats Card */}
            <Animatable.View
              animation="fadeInLeft"
              duration={800}
              delay={400}
              style={{
                backgroundColor: theme ? "#ffffff" : "#1e293b",
                borderRadius: 20,
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%'
              }}
            >
              <View style={{
                padding: 20,
                borderBottomWidth: 1,
                borderBottomColor: theme ? "#e2e8f0" : "#334155"
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{
                    backgroundColor: theme ? "#fef3c7" : "#451a03",
                    borderRadius: 8,
                    padding: 8,
                    marginRight: 12
                  }}>
                    <MaterialIcons
                      name="bar-chart"
                      size={20}
                      color={theme ? "#f59e0b" : "#fbbf24"}
                    />
                  </View>
                  <Text style={{
                    fontSize: 18,
                    fontWeight: '700',
                    color: theme ? "#1e293b" : "#f1f5f9"
                  }}>
                    Marketing Stats
                  </Text>
                </View>
              </View>

              <View style={{ padding: 20 }}>
                <SalesStatWidget loadingCallback={loadingCallback} />
              </View>
            </Animatable.View>
          </XStack>
          <AlertDialog open={open}>
            <AlertDialog.Portal>
              <AlertDialog.Overlay
                key="overlay"
                animation="quick"
                opacity={0.7}
                enterStyle={{ opacity: 0 }}
                exitStyle={{ opacity: 0 }}
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  backdropFilter: Platform.OS === 'web' ? 'blur(12px)' : undefined,
                }}
              />
              <AlertDialog.Content
                style={[
                  styles.modernCard,
                  theme ? styles.modernBgLight : styles.modernBgDark,
                  {
                    maxWidth: 500,
                    width: '90%',
                    alignSelf: 'center',
                    borderWidth: 1,
                    borderColor: theme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)',
                    ...(Platform.OS === 'web' && {
                      backdropFilter: 'blur(24px)',
                      boxShadow: '0 20px 64px rgba(0,0,0,0.15)',
                    }),
                  }
                ]}
              >
                {/* Modern Icon Header */}
                <View style={{
                  alignItems: 'center',
                  marginBottom: 20
                }}>
                  <View style={[
                    styles.modernIconButton,
                    {
                      width: 64,
                      height: 64,
                      borderRadius: 20,
                      backgroundColor: theme ? '#fef3c7' : '#451a03',
                      marginBottom: 16
                    }
                  ]}>
                    <MaterialIcons
                      name="warning"
                      size={32}
                      color={theme ? '#f59e0b' : '#fbbf24'}
                    />
                  </View>

                  <AlertDialog.Title style={[
                    styles.modernCardTitle,
                    {
                      fontSize: 24,
                      fontWeight: '700',
                      color: theme ? '#1e293b' : '#f1f5f9',
                      textAlign: 'center',
                      marginBottom: 0
                    }
                  ]}>
                    No Templates Found!
                  </AlertDialog.Title>
                </View>

                <AlertDialog.Description style={{
                  fontSize: 16,
                  lineHeight: 24,
                  color: theme ? '#64748b' : '#94a3b8',
                  textAlign: 'center',
                  marginBottom: 32,
                  paddingHorizontal: 8
                }}>
                  You have no templates to create a new campaign. Please create a template first before beginning a new campaign.
                </AlertDialog.Description>

                {/* Modern Action Buttons */}
                <XStack
                  gap={12}
                  justifyContent="center"
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Button
                    onPress={() => setOpen(false)}
                    style={[
                      styles.modernButton,
                      {
                        backgroundColor: theme ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.2)',
                        borderWidth: 1.5,
                        borderColor: theme ? '#94a3b8' : '#64748b',
                        minWidth: 120,
                        paddingHorizontal: 24,
                        ...(Platform.OS === 'web' && {
                          transition: 'all 0.2s ease',
                        }),
                      }
                    ]}
                  >
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 16,
                      fontWeight: '600'
                    }}>
                      Cancel
                    </Text>
                  </Button>

                  <Button
                    onPress={() => {
                      setOpen(false);
                      (navigation as any).navigate('Data', { screen: 'DataTemplates' });
                    }}
                    style={[
                      styles.modernButton,
                      {
                        backgroundColor: theme ? '#3b82f6' : '#1d4ed8',
                        borderWidth: 1.5,
                        borderColor: theme ? '#2563eb' : '#3b82f6',
                        minWidth: 140,
                        paddingHorizontal: 24,
                        shadowColor: '#3b82f6',
                        shadowOpacity: 0.3,
                        shadowRadius: 12,
                        elevation: 6,
                        ...(Platform.OS === 'web' && {
                          transition: 'all 0.2s ease',
                          boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                        }),
                      }
                    ]}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <MaterialIcons
                        name="folder"
                        size={18}
                        color="white"
                      />
                      <Text style={{
                        color: 'white',
                        fontSize: 16,
                        fontWeight: '600'
                      }}>
                        Go to Templates
                      </Text>
                    </View>
                  </Button>
                </XStack>
              </AlertDialog.Content>
            </AlertDialog.Portal>
          </AlertDialog>
          {/* Main Content Grid */}
          {/* Connected Platforms Card */}
          {
          Object.keys(userIntegrations).filter(item => allowedList.includes(item)).length > 0 &&
          <Animatable.View
            animation="fadeInLeft"
            duration={800}
            delay={200}
            style={{
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderRadius: 20,
              borderWidth: 1,
              borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
              overflow: 'hidden',
              width: '100%',
              maxWidth: '100%'
            }}
          >
            <View style={{
              padding: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? "#e2e8f0" : "#334155"
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0f172a",
                  borderRadius: 8,
                  padding: 8,
                  marginRight: 12
                }}>
                  <MaterialIcons
                    name="hub"
                    size={20}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={{
                    fontSize: 18,
                    fontWeight: '700',
                    color: theme ? "#1e293b" : "#f1f5f9"
                  }}>
                    Connected Platforms
                  </Text>
                  <Text style={{
                    fontSize: 14,
                    color: theme ? "#64748b" : "#94a3b8",
                    marginTop: 4
                  }}>
                    {Object.keys(userIntegrations).filter(item => allowedList.includes(item)).length > 0
                      ? `${Object.keys(userIntegrations).filter(item => allowedList.includes(item)).length} active integrations`
                      : "No connected platforms."
                    }
                  </Text>
                </View>
              </View>
            </View>

            <View style={{ padding: 20, minHeight: 200 }}>
              {Object.keys(userIntegrations).length > 0 ? (
                <FlatList
                  data={Object.keys(userIntegrations).filter(item => allowedList.includes(item))}
                  keyExtractor={(item, index) => `${item}-${index}`}
                  showsVerticalScrollIndicator={false}
                  horizontal
                  contentContainerStyle={{ gap: 12, justifyContent: 'center' }}
                  renderItem={({ item }) => {
                    const itemLogo =
                      (Array.isArray(originalIntegrationData)
                        ? originalIntegrationData.find((integration: { meta: string; logo?: string }) =>
                          integration.meta.toLowerCase() === item.toLowerCase()
                        )?.logo
                        : "") || "";
                    return (
                      <Animatable.View
                        animation="zoomIn"
                        delay={300}
                        style={{
                          flex: 1,
                          marginBottom: 12,
                          alignItems: 'center'
                        }}
                      >
                        <ConnectedPlatforms integration={item} logo={itemLogo} />
                      </Animatable.View>
                    );
                  }}
                />
              ) : (
                <View style={{
                  flex: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme ? "#f8fafc" : "#0f172a",
                  borderRadius: 12,
                  padding: 20
                }}>
                  <MaterialIcons
                    name="link-off"
                    size={48}
                    color={theme ? "#94a3b8" : "#475569"}
                    style={{ marginBottom: 12 }}
                  />
                  <Text style={{
                    color: theme ? "#64748b" : "#94a3b8",
                    fontSize: 16,
                    textAlign: 'center'
                  }}>
                    No platforms connected yet
                  </Text>
                </View>
              )}
            </View>
          </Animatable.View>
          }
          <View style={responsiveStyles.mainGrid}>

            {/* Right Column - Charts and Analytics */}
            <View style={responsiveStyles.rightColumn}>
              <YStack
                gap={24}
                style={{ width: '100%' }}
              >


                {/* Payment Provider Sales Statistics Components */}
                {effectiveProvider && (
                  <>
                    {/* Sales Stats Overview */}
                    <Animatable.View
                      animation="fadeInUp"
                      duration={800}
                      delay={300}
                    >
                      <SalesStatsOverview
                        stats={salesStats?.data}
                        isLoading={salesStatsLoading}
                      />
                    </Animatable.View>

                    {/* Sales Chart and Recent Transactions Row */}
                  <XStack flexWrap='wrap' style={{
                      gap: 16,
                      width: '100%',
                      overflow: 'hidden',
                    }}>
                      {/* Sales Chart */}
                      <Animatable.View
                        animation="fadeInLeft"
                        duration={800}
                        delay={400}
                        style={{
                          flex: (isTablet && !isSmallScreen) ? 2 : 1,
                          minWidth: 0, // Allow shrinking
                          flexBasis: 500,
                          width: '100%',
                          flexGrow: 1,
                          maxWidth: '100%'
                        }}
                      >
                        <SalesChart
                          data={monthlySalesData?.data || []}
                          isLoading={monthlySalesLoading}
                        />
                      </Animatable.View>

                      {/* Recent Transactions */}
                      <Animatable.View
                        animation="fadeInRight"
                        duration={800}
                        delay={500}
                        style={{
                          flex: (isTablet && !isSmallScreen) ? 1 : 1,
                          width: '100%',
                          flexBasis: 300,
                          flexGrow: 1,
                          maxWidth: '100%'
                        }}
                      >
                        <RecentTransactions
                          transactions={recentTransactions?.data || []}
                          isLoading={transactionsLoading}
                          onTransactionPress={(transaction) => {
                            // Handle transaction press - could open receipt or details
                            if (transaction.receiptUrl) {
                              // Open receipt URL
                              if (Platform.OS === 'web') {
                                window.open(transaction.receiptUrl, '_blank');
                              }
                            } else if (transaction.reference && effectiveProvider === 'paystack') {
                              // For PayStack, open transaction in dashboard
                              if (Platform.OS === 'web') {
                                window.open(`https://dashboard.paystack.com/#/transactions/${transaction.reference}`, '_blank');
                              }
                            }
                          }}
                        />
                      </Animatable.View>
                  </XStack>

                    {/* Payment Method Stats */}
                    <Animatable.View
                      animation="fadeInUp"
                      duration={800}
                      delay={600}
                    >
                      <PaymentMethodStats
                        stats={paymentMethodStats?.data || { paymentMethods: [], cardBrands: [] }}
                        isLoading={paymentStatsLoading}
                      />
                    </Animatable.View>
                  </>
                )}

                {/* Campaigns Overview Chart */}
                <Animatable.View
                  animation="fadeInRight"
                  duration={800}
                  delay={200}
                  style={{
                    backgroundColor: theme ? "#ffffff" : "#1e293b",
                    borderRadius: 20,
                    borderWidth: 1,
                    borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
                    overflow: 'hidden',
                    minHeight: 500
                  }}
                >
                  <View style={{
                    padding: 20,
                    borderBottomWidth: 1,
                    borderBottomColor: theme ? "#e2e8f0" : "#334155"
                  }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <View style={{
                        backgroundColor: theme ? "#ddd6fe" : "#312e81",
                        borderRadius: 8,
                        padding: 8,
                        marginRight: 12
                      }}>
                        <MaterialIcons
                          name="timeline"
                          size={20}
                          color={theme ? "#7c3aed" : "#a78bfa"}
                        />
                      </View>
                      <Text style={{
                        fontSize: 18,
                        fontWeight: '700',
                        color: theme ? "#1e293b" : "#f1f5f9"
                      }}>
                        Campaigns Overview
                      </Text>
                    </View>
                    <Text style={{
                      fontSize: 14,
                      color: theme ? "#64748b" : "#94a3b8",
                      marginTop: 4
                    }}>
                      Track campaign performance over time
                    </Text>
                  </View>

                  {Platform.OS === 'web' ? (
                    <View style={{ flex: 1, padding: 20 }}>
                      <DataSetChart
                        title="Campaign Analytics"
                        chartTitle="Performance Metrics"
                        dotLabel="Campaign Data"
                        minHeight={400}
                        chartType="category"
                      defaultDates={(() => {
                        // Create and validate dates
                        const startDate = new Date();
                        startDate.setDate(startDate.getDate() - 30); // 30 days ago
                        const endDate = new Date();

                        // Ensure dates are valid
                        const validStartDate = isNaN(startDate.getTime()) ? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) : startDate;
                        const validEndDate = isNaN(endDate.getTime()) ? new Date() : endDate;

                        return {
                          startDate: validStartDate,
                          endDate: validEndDate
                        };
                      })()}
                        dateCallback={() => { }}
                        controls={<></>}
                        chart={{
                          labels: [
                            '01/01/2025',
                            '02/01/2025',
                            '25/01/2025',
                            '04/01/2025',
                            '05/01/2025',
                            '06/01/2025',
                            '07/01/2025',
                            '08/01/2025',
                            '09/01/2025',
                            '10/01/2025',
                            '11/01/2025',
                          ],
                          colors: [theme ? "#7c3aed" : "#a78bfa", theme ? "#0ea5e9" : "#38bdf8", theme ? "#059669" : "#10b981"],
                          options: {
                            theme: {
                              mode: theme ? 'light' : 'dark'
                            }
                          },
                          series: [
                            {
                              name: 'Completed',
                              fill: 'solid',
                              data: [2, 1, 2, 2, 1, 2, 3, 2, 4, 2, 3],
                            },
                            {
                              name: 'Interactions',
                              fill: 'gradient',
                              data: [44, 55, 41, 67, 22, 43, 21, 41, 56, 27, 43],
                            },
                            {
                              name: 'Conversions',
                              fill: 'solid',
                              data: [30, 25, 36, 30, 45, 35, 64, 52, 59, 36, 39],
                            },
                          ],
                        }}
                        subheader={"Performance metrics"}
                      />
                    </View>
                  ) : (
                    <View style={{
                      flex: 1,
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 40
                    }}>
                      <LoadingIndicator color={theme ? "#7c3aed" : "#a78bfa"} type={undefined} />
                      <Text style={{
                        marginTop: 16,
                        color: theme ? "#64748b" : "#94a3b8",
                        fontSize: 16
                      }}>
                        Loading campaign data...
                      </Text>
                    </View>
                  )}
                </Animatable.View>
            </YStack>
          </View>
        </View>
      </DefaultScreen>
    )
  });

export default SalesDashboard;